/**
 * Inventory Tab - Rewritten for clarity and responsiveness.
 *
 * Layout: [Sidebar 45px] [Equipment ~22%] [Content fill]
 *
 * Sub-pages: Items, Character/Stats, Stones, Materials, Quests
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { AssetLoader } from '@client/core/AssetLoader';
import { GameItem, getItemColor, QuestItem } from '@shared/items/Item';
import { EquipmentSlot, EquipmentItem } from '@shared/types';
import { CurrencySlotType, CURRENCY_SLOT_INFO, getCurrencySlotInfo } from '@client/inventory/Inventory';
import { ItemTooltip } from '../ItemTooltip';
import type { EffectStone } from '@shared/skills/SkillCreator';
import { debugLog } from '@shared/utils/Logger';
import { CRAFTING_CURRENCIES, CraftingCurrencyType } from '@shared/crafting/CraftingSystem';
import { t } from '@locales/index';
import { getAudioManager } from '@client/audio/AudioManager';
import { SLOT_FALLBACK_ICONS } from '../ItemIcons';
import { getProceduralIcon, getDetailedProceduralIcon } from '@client/core/ProceduralItemIcons';

// ── Types ───────────────────────────────────────────────────────────────────

interface SlotInfo {
  x: number;
  y: number;
  size: number;
  index: number;
  type: 'inventory' | 'equipment' | 'currency';
  equipSlot?: EquipmentSlot;
  currencyType?: CurrencySlotType;
  tab?: number;
}

type FilterType = 'all' | 'weapon' | 'armor' | 'accessory';
type SortType = 'none' | 'rarity' | 'level' | 'type' | 'name';
type SubPageType = 'items' | 'character' | 'stones' | 'materials' | 'quests';

// ── Constants ───────────────────────────────────────────────────────────────

const SIDEBAR_W = 45;
const SIDEBAR_BTN = 38;
const SIDEBAR_GAP = 6;

const WEAPON_SLOTS: EquipmentSlot[] = ['weapon', 'offhand'];
const ARMOR_SLOTS: EquipmentSlot[] = ['head', 'chest', 'gloves', 'belt', 'boots', 'cape'];
const ACCESSORY_SLOTS: EquipmentSlot[] = ['amulet', 'ring1', 'ring2'];

const COLORS = {
  panelBg: '#1a1815',
  sectionBorder: '#2a2520',
  slotEmpty: '#151310',
  slotFilled: '#1a1815',
  slotBorderEmpty: '#333328',
  text: '#f4e8d4',
  label: '#888888',
  gold: '#ffd700',
  accent: '#d4af37',
  hover: '#2a2520',
} as const;

const CURRENCIES: CurrencySlotType[] = [
  'upgrade_orb', 'chaos_orb', 'divine_orb', 'regal_orb', 'exalted_orb',
  'socket_orb', 'link_orb', 'transmute_orb', 'alteration_orb', 'augment_orb',
];

const CRAFT_CURRENCIES: CraftingCurrencyType[] = [
  'demir_parcasi', 'ruh_damlasi', 'kam_boyasi', 'bereket_tozu', 'ugur_tozu',
  'temizleme_suyu', 'sabitleme_tasi', 'koruma_muhru', 'guclu_cekic', 'kutsal_yag',
];

// ── Helpers (pure functions) ────────────────────────────────────────────────

function subPages(): { id: SubPageType; icon: string; label: string }[] {
  return [
    { id: 'items', icon: '\u2694', label: t('inventory.items') },
    { id: 'character', icon: '\uD83D\uDCCA', label: t('classes.alp') },
    { id: 'stones', icon: '\uD83D\uDC8E', label: t('crafting.materials') },
    { id: 'materials', icon: '\uD83D\uDD27', label: t('crafting.materials') },
    { id: 'quests', icon: '\uD83D\uDCDC', label: t('inventory.questItems') },
  ];
}

function filterOptions(): { id: FilterType; label: string }[] {
  return [
    { id: 'all', label: t('inventory.filterAll') },
    { id: 'weapon', label: t('inventory.filterWeapon') },
    { id: 'armor', label: t('inventory.filterArmor') },
    { id: 'accessory', label: t('inventory.filterAccessory') },
  ];
}

function sortOptions(): { id: SortType; label: string }[] {
  return [
    { id: 'none', label: '-' },
    { id: 'rarity', label: t('inventory.sortRarity') },
    { id: 'level', label: t('inventory.sortLevel') },
    { id: 'type', label: t('inventory.sortType') },
    { id: 'name', label: t('inventory.sortName') },
  ];
}

function formatGold(gold: number): string {
  if (gold >= 1_000_000) return `${(gold / 1_000_000).toFixed(1)}M`;
  if (gold >= 1_000) return `${(gold / 1_000).toFixed(1)}K`;
  return gold.toString();
}

// ── Layout cache ────────────────────────────────────────────────────────────

interface Layout {
  scale: number;
  pad: number;
  equipW: number;
  equipSlot: number;
  invSlot: number;
  invAreaW: number;
  cols: number;
  currSlot: number;
  font: number;
  titleFont: number;
  btnH: number;
  tabBarH: number;
  // Precomputed positions
  sidebarX: number;
  equipX: number;
  contentX: number;
  contentY: number;
  contentH: number;
}

// ── Main class ──────────────────────────────────────────────────────────────

export class InventoryTab extends MenuTabContent {
  // Mouse state
  private hoveredSlot: SlotInfo | null = null;
  private selectedSlot: SlotInfo | null = null;
  private mx = 0;
  private my = 0;

  // UI state
  private filter: FilterType = 'all';
  private sort: SortType = 'none';
  private subPage: SubPageType = 'items';
  private hoveredBtn: string | null = null;
  private hoveredSubPage: string | null = null;
  private charScroll = 0;
  private hoveredStoneIdx = -1;
  private hoveredMatIdx = -1;

  // Delete confirmation
  private delSlot: { index: number; tab: number } | null = null;
  private delTimer = 0;
  private static readonly DEL_TIMEOUT = 2000;

  // Tooltip & assets
  private itemTooltip = new ItemTooltip();
  private assetLoader: AssetLoader | null = null;

  // Layout cache
  private _layout: Layout | null = null;
  private _layoutW = 0;
  private _layoutH = 0;

  constructor(player: Player, assetLoader?: AssetLoader) {
    super(player);
    this.assetLoader = assetLoader || null;
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 900, height: 600 };
  }

  update(dt: number): void {
    if (this.delSlot && this.delTimer > 0) {
      this.delTimer -= dt * 1000;
      if (this.delTimer <= 0) { this.delSlot = null; this.delTimer = 0; }
    }
  }

  onActivate(): void {
    this.hoveredSlot = null;
    this.selectedSlot = null;
    this.hoveredSubPage = null;
    this.charScroll = 0;
    this.hoveredStoneIdx = -1;
    this.hoveredMatIdx = -1;
    this.delSlot = null;
    this.delTimer = 0;
    this._layout = null;
  }

  // ── Layout ──────────────────────────────────────────────────────────────

  private getLayout(): Layout {
    const { width, height } = this.bounds;
    if (this._layout && this._layoutW === width && this._layoutH === height) return this._layout;

    const scale = Math.max(0.65, Math.min(1.0, width / 1000));
    const pad = Math.round(12 * scale);
    const usable = width - SIDEBAR_W - pad * 4;
    const equipW = Math.min(260, Math.max(170, usable * 0.22));
    const equipSlot = Math.min(50, Math.max(36, equipW / 4.5));
    const invAreaW = usable - equipW;
    const invSlot = Math.min(44, Math.max(32, invAreaW / 12));
    const cols = Math.max(8, Math.min(12, Math.floor(invAreaW / (invSlot + 4))));

    const { x, y } = this.bounds;
    const sidebarX = x + pad;
    const equipX = sidebarX + SIDEBAR_W + pad;
    const contentX = equipX + equipW + pad;
    const contentY = y + pad;
    const contentH = height - pad * 2;

    this._layout = {
      scale, pad, equipW, equipSlot,
      invSlot, invAreaW, cols,
      currSlot: Math.round(42 * scale),
      font: Math.max(10, Math.round(12 * scale)),
      titleFont: Math.max(12, Math.round(14 * scale)),
      btnH: Math.round(26 * scale),
      tabBarH: Math.round(30 * scale),
      sidebarX, equipX, contentX, contentY, contentH,
    };
    this._layoutW = width;
    this._layoutH = height;
    return this._layout;
  }

  // ── Main render ─────────────────────────────────────────────────────────

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const L = this.getLayout();

    this.renderSidebar(ctx, L);
    this.renderEquipment(ctx, L);
    this.renderSubPage(ctx, L);
    this.renderTooltip(ctx, L);
  }

  // ── Sidebar ─────────────────────────────────────────────────────────────

  private renderSidebar(ctx: CanvasRenderingContext2D, L: Layout): void {
    const sx = L.sidebarX;
    const sy = L.contentY;
    const h = L.contentH;

    // Background
    ctx.fillStyle = 'rgba(22,19,15,0.95)';
    this.drawRoundedRect(ctx, sx, sy, SIDEBAR_W, h, 8);
    ctx.fill();
    ctx.strokeStyle = '#3a3028';
    ctx.lineWidth = 1;
    ctx.stroke();

    const pages = subPages();
    const btnStartY = sy + 10;

    for (let i = 0; i < pages.length; i++) {
      const pg = pages[i];
      const bx = sx + (SIDEBAR_W - SIDEBAR_BTN) / 2;
      const by = btnStartY + i * (SIDEBAR_BTN + SIDEBAR_GAP);
      const active = this.subPage === pg.id;
      const hov = this.hoveredSubPage === pg.id;

      ctx.fillStyle = active ? 'rgba(50,42,30,0.95)' : hov ? 'rgba(35,30,22,0.9)' : 'rgba(20,18,14,0.8)';
      this.drawRoundedRect(ctx, bx, by, SIDEBAR_BTN, SIDEBAR_BTN, 6);
      ctx.fill();

      ctx.strokeStyle = active ? COLORS.accent : hov ? '#6a5a3a' : '#3a3028';
      ctx.lineWidth = active ? 1.5 : 1;
      ctx.stroke();

      if (active) {
        ctx.fillStyle = COLORS.accent;
        this.drawRoundedRect(ctx, bx, by + 4, 3, SIDEBAR_BTN - 8, 1);
        ctx.fill();
      }

      ctx.fillStyle = active ? COLORS.accent : hov ? '#bba060' : '#777';
      ctx.font = `${Math.floor(SIDEBAR_BTN * 0.45)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(pg.icon, bx + SIDEBAR_BTN / 2, by + SIDEBAR_BTN / 2);
    }
  }

  // ── Equipment panel ─────────────────────────────────────────────────────

  private renderEquipment(ctx: CanvasRenderingContext2D, L: Layout): void {
    const ex = L.equipX;
    const ey = L.contentY;
    const ew = L.equipW;
    const eh = L.contentH;
    const s = L.equipSlot;

    // Panel background
    const grad = ctx.createLinearGradient(ex, ey, ex, ey + eh);
    grad.addColorStop(0, 'rgba(25,22,18,0.95)');
    grad.addColorStop(0.5, 'rgba(18,15,12,0.98)');
    grad.addColorStop(1, 'rgba(12,10,8,0.95)');
    ctx.fillStyle = grad;
    this.drawRoundedRect(ctx, ex, ey, ew, eh, 10);
    ctx.fill();
    ctx.strokeStyle = '#4a3f2f';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(212,175,55,0.3)';
    ctx.lineWidth = 1;
    this.drawRoundedRect(ctx, ex + 3, ey + 3, ew - 6, eh - 6, 8);
    ctx.stroke();

    // Title
    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.titleFont}px Arial`;
    ctx.textAlign = 'center';
    const title = t('inventory.character');
    ctx.fillText(title, ex + ew / 2, ey + 22);
    const tw = ctx.measureText(title).width;
    ctx.strokeStyle = 'rgba(212,175,55,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ex + ew / 2 - tw / 2 - 10, ey + 28);
    ctx.lineTo(ex + ew / 2 + tw / 2 + 10, ey + 28);
    ctx.stroke();

    const cx = ex + ew / 2;
    const bodyY = ey + 42;

    // Character preview
    this.drawCharPreview(ctx, cx, bodyY + 120, L.scale);

    // Equipment slot positions (paper-doll layout)
    const positions = this.getEquipSlotPositions(cx, bodyY, s);

    // Connecting lines
    ctx.strokeStyle = 'rgba(80,70,50,0.3)';
    ctx.lineWidth = 1;
    for (const p of positions) {
      if (p.slot !== 'chest' && p.slot !== 'head' && p.slot !== 'belt' && p.slot !== 'boots') {
        ctx.beginPath();
        ctx.moveTo(p.x + s / 2, p.y + s / 2);
        ctx.lineTo(cx, p.y + s / 2);
        ctx.stroke();
      }
    }

    // Render each slot
    for (const p of positions) {
      const item = this.player.inventory.getEquippedItem(p.slot);
      const hov = this.hoveredSlot?.equipSlot === p.slot;
      const sel = this.selectedSlot?.equipSlot === p.slot;
      this.drawEquipmentSlot(ctx, p.x, p.y, s, item, hov, sel, p.slot);
    }

    // Mini stats summary
    const statsY = bodyY + s * 4 + 82;
    const statsH = eh - (statsY - ey) - 55;
    if (statsH > 30) this.drawMiniStats(ctx, ex + 10, statsY, ew - 20, statsH);

    // Gold bar at bottom
    const goldY = ey + eh - 42;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    this.drawRoundedRect(ctx, ex + 15, goldY, ew - 30, 34, 6);
    ctx.fill();
    ctx.strokeStyle = '#5a4a2a';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = COLORS.gold;
    ctx.font = `bold ${L.titleFont}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(`\uD83D\uDCB0 ${formatGold(this.player.inventory.getGold())}`, ex + ew / 2, goldY + 22);
  }

  private getEquipSlotPositions(cx: number, bodyY: number, s: number): { slot: EquipmentSlot; x: number; y: number }[] {
    return [
      { slot: 'head', x: cx - s / 2, y: bodyY },
      { slot: 'cape', x: cx - s - 35, y: bodyY + 10 },
      { slot: 'amulet', x: cx + 35, y: bodyY + 10 },
      { slot: 'weapon', x: cx - s - 45, y: bodyY + s + 25 },
      { slot: 'chest', x: cx - s / 2, y: bodyY + s + 15 },
      { slot: 'offhand', x: cx + 45, y: bodyY + s + 25 },
      { slot: 'ring1', x: cx - s - 45, y: bodyY + s * 2 + 40 },
      { slot: 'belt', x: cx - s / 2, y: bodyY + s * 2 + 35 },
      { slot: 'ring2', x: cx + 45, y: bodyY + s * 2 + 40 },
      { slot: 'gloves', x: cx - s - 45, y: bodyY + s * 3 + 55 },
      { slot: 'boots', x: cx - s / 2, y: bodyY + s * 3 + 55 },
    ];
  }

  private drawCharPreview(ctx: CanvasRenderingContext2D, cx: number, cy: number, scale: number): void {
    const pw = Math.floor(128 * scale);
    const ph = Math.floor(128 * scale);
    if (this.player.renderPreview(ctx, cx - pw / 2, cy - ph * 0.6, pw, ph)) return;

    // Fallback silhouette
    ctx.save();
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = COLORS.accent;
    const s = scale * 0.8;
    ctx.beginPath(); ctx.arc(cx, cy - 85 * s, 18 * s, 0, Math.PI * 2); ctx.fill();
    ctx.fillRect(cx - 6 * s, cy - 67 * s, 12 * s, 12 * s);
    ctx.beginPath();
    ctx.moveTo(cx - 28 * s, cy - 55 * s);
    ctx.lineTo(cx + 28 * s, cy - 55 * s);
    ctx.lineTo(cx + 22 * s, cy + 20 * s);
    ctx.lineTo(cx - 22 * s, cy + 20 * s);
    ctx.closePath(); ctx.fill();
    ctx.fillRect(cx - 45 * s, cy - 50 * s, 15 * s, 55 * s);
    ctx.fillRect(cx + 30 * s, cy - 50 * s, 15 * s, 55 * s);
    ctx.fillRect(cx - 18 * s, cy + 20 * s, 14 * s, 50 * s);
    ctx.fillRect(cx + 4 * s, cy + 20 * s, 14 * s, 50 * s);
    ctx.restore();
  }

  private drawMiniStats(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    this.drawRoundedRect(ctx, x, y, w, h, 5);
    ctx.fill();

    const stats = this.player.stats;
    const fs = Math.max(9, Math.min(11, w / 18));
    const items = [
      { lbl: 'CAN', val: `${stats.hp}/${stats.maxHp}`, clr: '#ff6666' },
      { lbl: 'MANA', val: `${stats.mana}/${stats.maxMana}`, clr: '#6699ff' },
      { lbl: 'ZIRH', val: `${stats.armor}`, clr: '#aaa' },
    ];
    const lh = Math.min(16, h / items.length);
    let sy = y + 12;
    ctx.font = `${fs}px Arial`;
    for (const s of items) {
      if (sy + lh > y + h) break;
      ctx.fillStyle = '#666'; ctx.textAlign = 'left'; ctx.fillText(s.lbl, x + 8, sy);
      ctx.fillStyle = s.clr; ctx.textAlign = 'right'; ctx.fillText(s.val, x + w - 8, sy);
      sy += lh;
    }
  }

  // ── Sub-page router ─────────────────────────────────────────────────────

  private renderSubPage(ctx: CanvasRenderingContext2D, L: Layout): void {
    const sx = L.contentX;
    const sy = L.contentY;
    const w = L.invAreaW;
    const h = L.contentH;

    ctx.fillStyle = 'rgba(18,16,12,0.85)';
    this.drawRoundedRect(ctx, sx, sy, w, h, 8);
    ctx.fill();
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.stroke();

    switch (this.subPage) {
      case 'items': this.renderItemsPage(ctx, sx, sy, w, h, L); break;
      case 'character': this.renderCharacterPage(ctx, sx, sy, w, h, L); break;
      case 'stones': this.renderStonesPage(ctx, sx, sy, w, h, L); break;
      case 'materials': this.renderMaterialsPage(ctx, sx, sy, w, h, L); break;
      case 'quests': this.renderQuestsPage(ctx, sx, sy, w, h, L); break;
    }
  }

  // ── Items page ──────────────────────────────────────────────────────────

  private renderItemsPage(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, h: number, L: Layout): void {
    let hy = sy + 8;

    // Tab bar
    this.renderTabBar(ctx, sx + 10, hy, w - 20, L);
    hy += L.tabBarH + 8;

    // Filter / sort controls
    this.renderControls(ctx, sx + 10, hy, w - 20, L);
    hy += L.btnH + 12;

    // Inventory grid
    const gap = 4;
    const s = L.invSlot;
    const maxSlots = this.player.inventory.getMaxSlots();
    const rows = Math.ceil(maxSlots / L.cols);
    const gridH = rows * (s + gap) + 16;
    this.renderInventoryGrid(ctx, sx + 10, hy, w - 20, L);

    // Materials section below
    const matY = hy + gridH + 12;
    const matH = h - (matY - sy) - 12;
    if (matH > 50) this.renderCurrencyBar(ctx, sx + 10, matY, w - 20, matH, L);
  }

  private renderTabBar(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, L: Layout): void {
    const tabCount = this.player.inventory.getTabCount();
    const active = this.player.inventory.getActiveTab();
    const canBuy = this.player.inventory.canPurchaseTab();
    const tw = Math.min(60, (w - 80) / Math.max(tabCount + 1, 5));
    const gap = 4;

    for (let i = 0; i < tabCount; i++) {
      const tx = sx + i * (tw + gap);
      const isA = i === active;
      const isH = this.hoveredBtn === `tab_${i}`;

      ctx.fillStyle = isA ? '#3d3228' : isH ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, tx, sy, tw, L.tabBarH, 4);
      ctx.fill();
      if (isA) { ctx.strokeStyle = COLORS.accent; ctx.lineWidth = 1; ctx.stroke(); }

      ctx.fillStyle = isA ? COLORS.accent : COLORS.label;
      ctx.font = `${isA ? 'bold ' : ''}${L.font}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${i + 1}`, tx + tw / 2, sy + L.tabBarH / 2);
    }

    // Add tab button
    if (tabCount < 10) {
      const ax = sx + tabCount * (tw + gap);
      const isH = this.hoveredBtn === 'tab_add';

      if (isH && canBuy) { ctx.shadowColor = '#88ff88'; ctx.shadowBlur = 8; }
      ctx.fillStyle = canBuy ? (isH ? '#2a4020' : '#1a2815') : COLORS.panelBg;
      this.drawRoundedRect(ctx, ax, sy, tw, L.tabBarH, 4);
      ctx.fill();
      ctx.strokeStyle = canBuy ? (isH ? '#88ff88' : '#4a6030') : COLORS.sectionBorder;
      ctx.lineWidth = isH && canBuy ? 2 : 1;
      ctx.stroke();
      ctx.shadowBlur = 0;

      ctx.fillStyle = canBuy ? '#88ff88' : '#444';
      ctx.font = `bold ${L.font + 2}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('+', ax + tw / 2, sy + L.tabBarH / 2);

      if (isH) {
        const cost = this.player.inventory.getTabPurchaseCost();
        const txt = `${cost.toLocaleString()} Altun`;
        ctx.font = `bold ${L.font - 1}px Arial`;
        const ttW = ctx.measureText(txt).width + 16;
        const ttX = ax + tw / 2 - ttW / 2;
        const ttY = sy + L.tabBarH + 4;
        ctx.fillStyle = 'rgba(20,16,10,0.95)';
        this.drawRoundedRect(ctx, ttX, ttY, ttW, 24, 4);
        ctx.fill();
        ctx.strokeStyle = canBuy ? COLORS.gold : '#ff4444';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = canBuy ? COLORS.gold : '#ff4444';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(txt, ttX + ttW / 2, ttY + 12);
      }
    }

    // Slot count
    const used = this.player.inventory.getUsedSlots();
    const max = this.player.inventory.getMaxSlots();
    ctx.fillStyle = '#666';
    ctx.font = `${L.font}px Arial`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${used}/${max}`, sx + w - 5, sy + L.tabBarH / 2);
  }

  private renderControls(ctx: CanvasRenderingContext2D, sx: number, sy: number, _w: number, L: Layout): void {
    let bx = sx;

    ctx.fillStyle = '#666';
    ctx.font = `${L.font - 1}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${t('lootFilter.label')}:`, bx, sy + L.btnH / 2);
    bx += 45;

    for (const f of filterOptions()) {
      const bw = ctx.measureText(f.label).width + 16;
      const isA = this.filter === f.id;
      const isH = this.hoveredBtn === `filter_${f.id}`;

      ctx.fillStyle = isA ? '#3d3228' : isH ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, bx, sy, bw, L.btnH, 4);
      ctx.fill();
      if (isA) { ctx.strokeStyle = COLORS.accent; ctx.lineWidth = 1; ctx.stroke(); }

      ctx.fillStyle = isA ? COLORS.accent : '#aaa';
      ctx.font = `${L.font - 1}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(f.label, bx + bw / 2, sy + L.btnH / 2);
      bx += bw + 4;
    }

    bx += 15;
    ctx.fillStyle = '#666';
    ctx.textAlign = 'left';
    ctx.fillText('Sirala:', bx, sy + L.btnH / 2);
    bx += 45;

    for (const s of sortOptions()) {
      const bw = ctx.measureText(s.label).width + 16;
      const isA = this.sort === s.id;
      const isH = this.hoveredBtn === `sort_${s.id}`;

      ctx.fillStyle = isA ? '#3d3228' : isH ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, bx, sy, bw, L.btnH, 4);
      ctx.fill();
      if (isA) { ctx.strokeStyle = '#6688bb'; ctx.lineWidth = 1; ctx.stroke(); }

      ctx.fillStyle = isA ? '#88aadd' : '#aaa';
      ctx.font = `${L.font - 1}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(s.label, bx + bw / 2, sy + L.btnH / 2);
      bx += bw + 4;
    }
  }

  private renderInventoryGrid(ctx: CanvasRenderingContext2D, sx: number, sy: number, areaW: number, L: Layout): void {
    const gap = 4;
    const s = L.invSlot;
    const cols = L.cols;
    const maxSlots = this.player.inventory.getMaxSlots();
    const rows = Math.ceil(maxSlots / cols);
    const items = this.getFilteredItems();
    const gridW = cols * (s + gap) - gap;
    const gx = sx + (areaW - gridW) / 2;
    const activeTab = this.player.inventory.getActiveTab();

    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    this.drawRoundedRect(ctx, gx - 6, sy - 6, gridW + 12, rows * (s + gap) + 8, 6);
    ctx.fill();

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        if (idx >= maxSlots) continue;
        const x = gx + c * (s + gap);
        const y = sy + r * (s + gap);
        const item = items[idx] || null;
        const hov = this.hoveredSlot?.type === 'inventory' && this.hoveredSlot.index === idx;
        const sel = this.selectedSlot?.type === 'inventory' && this.selectedSlot.index === idx;
        const isDel = this.delSlot !== null && this.delSlot.index === idx && this.delSlot.tab === activeTab;

        this.drawSlot(ctx, x, y, s, item, hov, sel);

        // Delete confirmation overlay
        if (isDel && item) {
          ctx.strokeStyle = '#ff3333'; ctx.lineWidth = 2;
          this.drawRoundedRect(ctx, x, y, s, s, 4); ctx.stroke();
          ctx.fillStyle = 'rgba(255,0,0,0.25)';
          this.drawRoundedRect(ctx, x, y, s, s, 4); ctx.fill();
          ctx.fillStyle = '#ff4444';
          ctx.font = `bold ${Math.max(9, s * 0.28)}px Arial`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText('Sil?', x + s / 2, y + s / 2);
        } else if (hov && item && !sel) {
          // Delete "X" button on hover
          const isQuest = 'itemType' in item && (item as { itemType: string }).itemType === 'quest';
          if (!isQuest) {
            const bs = Math.max(12, s * 0.3);
            const bx = x + s - bs - 1;
            const by = y + 1;
            ctx.fillStyle = 'rgba(180,30,30,0.9)';
            ctx.beginPath(); ctx.arc(bx + bs / 2, by + bs / 2, bs / 2, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.font = `bold ${Math.max(8, bs * 0.65)}px Arial`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText('X', bx + bs / 2, by + bs / 2);
          }
        }
      }
    }
  }

  private renderCurrencyBar(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, h: number, L: Layout): void {
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    this.drawRoundedRect(ctx, sx, sy, w, h, 6);
    ctx.fill();

    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.titleFont}px Arial`;
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText(t('inventory.materials'), sx + 10, sy + 8);

    const ss = L.currSlot;
    const gap = 8;
    const cols = Math.floor((w - 20) / (ss + gap));
    const gy = sy + 32;
    const gsx = sx + 10;

    for (let i = 0; i < CURRENCIES.length; i++) {
      const type = CURRENCIES[i];
      const info = CURRENCY_SLOT_INFO[type];
      const amt = this.player.inventory.getCurrencyAmount(type);
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gsx + col * (ss + gap);
      const y = gy + row * (ss + gap + 14);
      const hov = this.hoveredSlot?.type === 'currency' && this.hoveredSlot.currencyType === type;

      ctx.fillStyle = hov ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, x, y, ss, ss, 4);
      ctx.fill();
      ctx.strokeStyle = amt > 0 ? info.color + '80' : COLORS.sectionBorder;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = amt > 0 ? info.color : '#444';
      ctx.font = `${ss * 0.5}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(info.icon, x + ss / 2, y + ss / 2 - 2);

      if (amt > 0) {
        ctx.fillStyle = 'rgba(0,0,0,0.8)';
        const bw = Math.max(16, ctx.measureText(amt.toString()).width + 6);
        this.drawRoundedRect(ctx, x + ss - bw + 2, y + ss - 14, bw, 14, 3);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(9, ss * 0.26)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(amt > 9999 ? '9999+' : amt.toString(), x + ss - bw / 2 + 2, y + ss - 7);
      }

      ctx.fillStyle = '#666';
      ctx.font = `${Math.max(8, L.font - 3)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(getCurrencySlotInfo(type).name.split(' ')[0], x + ss / 2, y + ss + 2);
    }
  }

  // ── Character / Stats page ──────────────────────────────────────────────

  private renderCharacterPage(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, _h: number, L: Layout): void {
    const pad = 15;
    const cx = sx + pad;
    const cw = w - pad * 2;
    let dy = sy + pad - this.charScroll;

    // Title
    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.titleFont + 2}px Arial`;
    ctx.textAlign = 'center';
    const title = t('inventory.characterStats');
    ctx.fillText(title, sx + w / 2, dy + 14);
    const tw = ctx.measureText(title).width;
    ctx.strokeStyle = 'rgba(212,175,55,0.5)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(sx + w / 2 - tw / 2 - 15, dy + 24); ctx.lineTo(sx + w / 2 + tw / 2 + 15, dy + 24); ctx.stroke();
    dy += 36;

    const stats = this.player.stats;
    const barW = Math.min(280, cw - 20);
    const barH = 16;
    const barX = cx + (cw - barW) / 2;

    // Resources
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.resources'), L);
    dy = this.drawBar(ctx, barX, dy, barW, barH, 'CAN', stats.hp, stats.maxHp, '#cc3333', '#661111', L) + 6;
    dy = this.drawBar(ctx, barX, dy, barW, barH, 'MANA', stats.mana, stats.maxMana, '#3366cc', '#112266', L) + 6;
    dy = this.drawBar(ctx, barX, dy, barW, barH, 'ENERJI KALKANI', stats.energyShield, stats.maxEnergyShield, '#8844aa', '#442266', L) + 16;

    // Offensive stats
    const calc = this.player.getStatCalculator();
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.offense'), L);
    const offStats = [
      { l: t('items.mods.damage'), v: `${Math.floor(calc.calculate('damage', 0))}`, c: '#ff8866' },
      { l: t('items.mods.attackSpeed'), v: `${Math.floor(calc.calculate('attackSpeed', 100))}%`, c: '#ffaa44' },
      { l: t('items.mods.critChance'), v: `${Math.floor(calc.calculate('critChance', 5))}%`, c: '#ffdd44' },
      { l: t('items.mods.critDamage'), v: `${Math.floor(calc.calculate('critDamage', 150))}%`, c: '#ffcc33' },
      { l: t('items.mods.spellDamage'), v: `${Math.floor(calc.calculate('spellDamage', 100))}%`, c: '#aa88ff' },
      { l: t('items.mods.moveSpeed'), v: `${Math.floor(calc.calculate('moveSpeed', 100))}%`, c: '#88ffaa' },
    ];
    for (const s of offStats) dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, s.l, s.v, s.c, L);
    dy += 10;

    // Defensive stats
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.defense'), L);
    const defStats = [
      { l: t('items.mods.armor'), v: `${stats.armor}`, c: '#aaa' },
      { l: t('items.mods.evasion'), v: `${stats.evasion}`, c: '#88cc88' },
      { l: t('items.mods.energyShield'), v: `${stats.maxEnergyShield}`, c: '#8844aa' },
    ];
    for (const s of defStats) dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, s.l, s.v, s.c, L);
    dy += 10;

    // Regen
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.regeneration'), L);
    const regenStats = [
      { l: t('items.mods.hpRegen'), v: `${+(2 + calc.calculate('hpRegen', 0)).toFixed(1)}/s`, c: '#ff6666' },
      { l: t('items.mods.manaRegen'), v: `${+(2 + calc.calculate('manaRegen', 0)).toFixed(1)}/s`, c: '#6666ff' },
    ];
    for (const s of regenStats) dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, s.l, s.v, s.c, L);
    dy += 10;

    // Resistances
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.elementResist'), L);
    const res = this.player.resistances;
    const maxR = 75;
    const resBarW = Math.min(220, cw - 80);
    const resBarX = cx + cw - resBarW - 15;
    const resistData = [
      { l: t('elements.physical'), v: res.physical, c: '#ccc', bg: '#333' },
      { l: t('elements.gok'), v: res.gok, c: '#88bbff', bg: '#223355' },
      { l: t('elements.od'), v: res.od, c: '#ff8844', bg: '#553322' },
      { l: t('elements.su'), v: res.su, c: '#44dddd', bg: '#224444' },
      { l: t('elements.karanlik'), v: res.karanlik, c: '#bb66ff', bg: '#442255' },
    ];
    for (const r of resistData) {
      ctx.fillStyle = '#999'; ctx.font = `${L.font}px Arial`;
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText(r.l, cx + 10, dy + barH / 2);

      ctx.fillStyle = r.bg;
      this.drawRoundedRect(ctx, resBarX, dy, resBarW, barH, 3); ctx.fill();
      const pct = Math.min(1, Math.max(0, r.v / maxR));
      if (pct > 0) { ctx.fillStyle = r.c; this.drawRoundedRect(ctx, resBarX, dy, resBarW * pct, barH, 3); ctx.fill(); }

      ctx.fillStyle = '#fff'; ctx.font = `bold ${L.font - 1}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`${r.v}%`, resBarX + resBarW / 2, dy + barH / 2);
      dy += barH + 6;
    }
    dy += 10;

    // Attributes
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.attributes'), L);
    const attrs = [
      { l: t('items.mods.strength'), v: `${stats.strength}`, c: '#ff6644' },
      { l: t('items.mods.agility'), v: `${stats.agility}`, c: '#44ff66' },
      { l: t('items.mods.spirit'), v: `${stats.spirit}`, c: '#6688ff' },
    ];
    for (const s of attrs) dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, s.l, s.v, s.c, L);
    dy += 10;

    // Equipment bonuses
    dy = this.drawSectionHead(ctx, cx, dy, cw, t('inventory.equipBonuses'), L);
    const eqStats = this.player.inventory.getEquipmentStats();
    const flatE = Object.entries(eqStats.flat);
    const incE = Object.entries(eqStats.increased);

    if (flatE.length === 0 && incE.length === 0) {
      ctx.fillStyle = '#555'; ctx.font = `${L.font}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(t('inventory.noEquipBonuses'), sx + w / 2, dy);
    } else {
      const names = this.getStatNames();
      for (const [k, v] of flatE) {
        if (v === 0) continue;
        dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, names[k] || k, `${v > 0 ? '+' : ''}${v}`, v > 0 ? '#88ff88' : '#ff8888', L);
      }
      for (const [k, v] of incE) {
        if (v === 0) continue;
        dy = this.drawStatRow(ctx, cx + 10, dy, cw - 20, names[k] || k, `${v > 0 ? '+' : ''}${v}%`, v > 0 ? '#88ff88' : '#ff8888', L);
      }
    }
  }

  // ── Stones page ─────────────────────────────────────────────────────────

  private renderStonesPage(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, h: number, L: Layout): void {
    const pad = 15;

    this.drawPageTitle(ctx, sx, sy, w, pad, t('inventory.effectStones'), L);

    const stones = this.player.inventory.getEffectStones();
    const gridY = sy + pad + 38;
    const elements: { id: 'od' | 'gok' | 'su' | 'karanlik'; label: string; color: string }[] = [
      { id: 'od', label: 'Od', color: '#ff4422' },
      { id: 'gok', label: 'Gok', color: '#4488ff' },
      { id: 'su', label: 'Su', color: '#44dddd' },
      { id: 'karanlik', label: 'Karanlik', color: '#cc88ff' },
    ];

    const colW = (w - pad * 2) / 4;
    const ss = Math.min(48, colW - 12);

    // Column headers
    for (let c = 0; c < 4; c++) {
      ctx.fillStyle = elements[c].color;
      ctx.font = `bold ${L.font}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(elements[c].label, sx + pad + c * colW + colW / 2, gridY);
    }

    const rowY = gridY + 22;
    const stonesByEl: Record<string, EffectStone[]> = { od: [], gok: [], su: [], yer: [], kut: [] };
    for (const st of stones) if (stonesByEl[st.type]) stonesByEl[st.type].push(st);

    const maxRows = Math.floor((h - (rowY - sy) - 20) / (ss + 8));
    this.hoveredStoneIdx = -1;

    for (let c = 0; c < 4; c++) {
      const elem = elements[c];
      const colX = sx + pad + c * colW + (colW - ss) / 2;
      const elStones = stonesByEl[elem.id];

      for (let r = 0; r < Math.max(3, elStones.length); r++) {
        if (r >= maxRows) break;
        const x = colX;
        const y = rowY + r * (ss + 8);
        const stone = r < elStones.length ? elStones[r] : null;
        const hov = this.mx >= x && this.mx <= x + ss && this.my >= y && this.my <= y + ss;

        if (hov && stone) this.hoveredStoneIdx = stones.indexOf(stone);

        ctx.fillStyle = stone ? (hov ? 'rgba(40,35,25,0.9)' : 'rgba(25,22,18,0.9)') : 'rgba(15,13,10,0.5)';
        this.drawRoundedRect(ctx, x, y, ss, ss, 5);
        ctx.fill();

        if (stone) {
          const rc = getItemColor(stone.rarity);
          ctx.strokeStyle = hov ? rc : rc + '99';
          ctx.lineWidth = hov ? 2 : 1.5;
          if (stone.rarity !== 'normal' && stone.rarity !== 'magic') {
            ctx.shadowColor = rc; ctx.shadowBlur = hov ? 10 : 4;
          }
        } else {
          ctx.strokeStyle = 'rgba(60,50,35,0.4)'; ctx.lineWidth = 1;
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

        if (stone) {
          const center = { x: x + ss / 2, y: y + ss / 2 - 4 };
          const rad = ss * 0.25;
          const g = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, rad);
          g.addColorStop(0, elem.color); g.addColorStop(1, elem.color + '44');
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(center.x, center.y, rad, 0, Math.PI * 2); ctx.fill();

          ctx.fillStyle = '#ccc';
          ctx.font = `${Math.max(8, L.font - 3)}px Arial`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
          const name = stone.name.length > 8 ? stone.name.substring(0, 7) + '..' : stone.name;
          ctx.fillText(name, x + ss / 2, y + ss - 2);
        } else {
          ctx.fillStyle = 'rgba(60,50,35,0.3)';
          ctx.font = `${ss * 0.35}px Arial`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText('\u25C7', x + ss / 2, y + ss / 2);
        }
      }
    }

    if (stones.length === 0) {
      ctx.fillStyle = '#555'; ctx.font = `${L.font + 1}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Etki tasi yok', sx + w / 2, sy + h / 2);
    }
  }

  // ── Materials page ──────────────────────────────────────────────────────

  private renderMaterialsPage(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, _h: number, L: Layout): void {
    const pad = 15;
    this.drawPageTitle(ctx, sx, sy, w, pad, 'MALZEMELER', L);

    // Gold display
    const goldY = sy + pad + 38;
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    this.drawRoundedRect(ctx, sx + pad, goldY, w - pad * 2, 50, 8);
    ctx.fill();
    ctx.strokeStyle = '#5a4a2a'; ctx.lineWidth = 1; ctx.stroke();

    ctx.fillStyle = COLORS.gold;
    ctx.font = `bold ${L.titleFont + 6}px Arial`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(`\uD83D\uDCB0 ${formatGold(this.player.inventory.getGold())} Altun`, sx + w / 2, goldY + 25);

    // Currency orbs
    const ss = 56;
    const gap = 12;
    const gridY = goldY + 65;
    const gridX = sx + pad;
    const avail = w - pad * 2;
    const cols = Math.floor(avail / (ss + gap));
    this.hoveredMatIdx = -1;

    for (let i = 0; i < CURRENCIES.length; i++) {
      const type = CURRENCIES[i];
      const info = CURRENCY_SLOT_INFO[type];
      const amt = this.player.inventory.getCurrencyAmount(type);
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gridX + col * (ss + gap) + (avail - cols * (ss + gap) + gap) / 2;
      const y = gridY + row * (ss + gap + 20);
      const hov = this.mx >= x && this.mx <= x + ss && this.my >= y && this.my <= y + ss;

      if (hov) this.hoveredMatIdx = i;

      ctx.fillStyle = hov ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, x, y, ss, ss, 6);
      ctx.fill();
      ctx.strokeStyle = amt > 0 ? info.color + '80' : COLORS.sectionBorder;
      ctx.lineWidth = hov ? 2 : 1;
      ctx.stroke();

      if (hov && amt > 0) { ctx.shadowColor = info.color; ctx.shadowBlur = 8; ctx.strokeStyle = info.color; ctx.stroke(); ctx.shadowBlur = 0; }

      ctx.fillStyle = amt > 0 ? info.color : '#444';
      ctx.font = `${ss * 0.45}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(info.icon, x + ss / 2, y + ss / 2 - 4);

      ctx.fillStyle = 'rgba(0,0,0,0.85)';
      const bw = Math.max(20, ctx.measureText(amt.toString()).width + 8);
      this.drawRoundedRect(ctx, x + ss - bw + 2, y + ss - 16, bw, 16, 3);
      ctx.fill();
      ctx.fillStyle = amt > 0 ? '#fff' : '#555';
      ctx.font = `bold ${Math.max(10, ss * 0.22)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(amt > 9999 ? '9999+' : amt.toString(), x + ss - bw / 2 + 2, y + ss - 8);

      ctx.fillStyle = amt > 0 ? '#aaa' : '#555';
      ctx.font = `${Math.max(9, L.font - 2)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(getCurrencySlotInfo(type).name, x + ss / 2, y + ss + 3);
    }

    // Crafting currencies section
    const tradeRows = Math.ceil(CURRENCIES.length / cols);
    const craftY = gridY + tradeRows * (ss + gap + 20) + 15;

    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.font + 1}px Arial`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText('ZANAAT MALZEMELERI', sx + w / 2, craftY);
    const ctw = ctx.measureText('ZANAAT MALZEMELERI').width;
    ctx.strokeStyle = 'rgba(212,175,55,0.3)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(sx + w / 2 - ctw / 2 - 10, craftY + 16); ctx.lineTo(sx + w / 2 + ctw / 2 + 10, craftY + 16); ctx.stroke();

    const cs = 48;
    const cGap = 10;
    const cGridY = craftY + 24;
    const cCols = Math.floor(avail / (cs + cGap));
    const rarCols: Record<string, string> = { normal: '#aaa', magic: '#4488ff', rare: '#ffdd44', sacred: '#44ff88', legendary: '#ff8844' };

    for (let i = 0; i < CRAFT_CURRENCIES.length; i++) {
      const cType = CRAFT_CURRENCIES[i];
      const cInfo = CRAFTING_CURRENCIES[cType];
      const cAmt = this.player.inventory.getCraftingCurrencyCount(cType);
      const col = i % cCols;
      const row = Math.floor(i / cCols);
      const x = gridX + col * (cs + cGap) + (avail - cCols * (cs + cGap) + cGap) / 2;
      const y = cGridY + row * (cs + cGap + 18);
      const hov = this.mx >= x && this.mx <= x + cs && this.my >= y && this.my <= y + cs;
      const rClr = rarCols[cInfo.rarity] || '#aaa';

      ctx.fillStyle = hov ? COLORS.hover : COLORS.panelBg;
      this.drawRoundedRect(ctx, x, y, cs, cs, 6);
      ctx.fill();
      ctx.strokeStyle = cAmt > 0 ? rClr + '80' : COLORS.sectionBorder;
      ctx.lineWidth = hov ? 2 : 1;
      ctx.stroke();

      if (hov && cAmt > 0) { ctx.shadowColor = rClr; ctx.shadowBlur = 8; ctx.strokeStyle = rClr; ctx.stroke(); ctx.shadowBlur = 0; }

      ctx.fillStyle = cAmt > 0 ? '#fff' : '#444';
      ctx.font = `${cs * 0.45}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(cInfo.icon, x + cs / 2, y + cs / 2 - 2);

      ctx.fillStyle = 'rgba(0,0,0,0.85)';
      const cbw = Math.max(18, ctx.measureText(cAmt.toString()).width + 6);
      this.drawRoundedRect(ctx, x + cs - cbw + 2, y + cs - 14, cbw, 14, 3);
      ctx.fill();
      ctx.fillStyle = cAmt > 0 ? '#fff' : '#555';
      ctx.font = `bold ${Math.max(9, cs * 0.2)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(cAmt.toString(), x + cs - cbw / 2 + 2, y + cs - 7);

      ctx.fillStyle = cAmt > 0 ? '#aaa' : '#555';
      ctx.font = `${Math.max(8, L.font - 3)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(cInfo.name, x + cs / 2, y + cs + 2);

      if (hov) {
        const tx = x + cs + 8;
        const ty = y;
        ctx.fillStyle = 'rgba(20,16,10,0.95)';
        this.drawRoundedRect(ctx, tx, ty, 180, 50, 5);
        ctx.fill();
        ctx.strokeStyle = rClr; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = rClr; ctx.font = `bold ${L.font}px Arial`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.fillText(cInfo.name, tx + 8, ty + 8);
        ctx.fillStyle = '#888'; ctx.font = `${L.font - 1}px Arial`;
        ctx.fillText(cInfo.description, tx + 8, ty + 28);
      }
    }
  }

  // ── Quests page ─────────────────────────────────────────────────────────

  private renderQuestsPage(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, h: number, L: Layout): void {
    const pad = 15;
    this.drawPageTitle(ctx, sx, sy, w, pad, 'GOREV ESYALARI', L);

    const questItems: { item: QuestItem; tab: number; index: number }[] = [];
    for (const entry of this.player.inventory.getAllItemsAllTabs()) {
      if ('questId' in entry.item) questItems.push({ item: entry.item as unknown as QuestItem, tab: entry.tab, index: entry.index });
    }

    if (questItems.length === 0) {
      ctx.fillStyle = '#666'; ctx.font = `${L.font + 2}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(t('inventory.noQuestItems'), sx + w / 2, sy + h / 2);
      return;
    }

    let dy = sy + pad + 40;
    const itemH = 65;

    for (const qe of questItems) {
      if (dy + itemH > sy + h - 10) break;
      const qi = qe.item;

      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      this.drawRoundedRect(ctx, sx + pad, dy, w - pad * 2, itemH, 6);
      ctx.fill();
      ctx.strokeStyle = '#ff884466'; ctx.lineWidth = 1; ctx.stroke();

      ctx.fillStyle = '#ff8844'; ctx.font = '24px Arial';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('\uD83D\uDCDC', sx + pad + 25, dy + itemH / 2);

      ctx.fillStyle = getItemColor(qi.rarity);
      ctx.font = `bold ${L.font + 1}px Arial`;
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      ctx.fillText(qi.name, sx + pad + 50, dy + 10);

      ctx.fillStyle = '#ff8844'; ctx.font = `${L.font - 1}px Arial`;
      ctx.fillText(`Gorev: ${qi.questId}`, sx + pad + 50, dy + 10 + L.font + 4);

      if (qi.description) {
        ctx.fillStyle = '#888'; ctx.font = `${L.font - 1}px Arial`;
        const desc = qi.description.length > 60 ? qi.description.substring(0, 57) + '...' : qi.description;
        ctx.fillText(desc, sx + pad + 50, dy + 10 + (L.font + 4) * 2);
      }

      dy += itemH + 8;
    }
  }

  // ── Shared drawing helpers ──────────────────────────────────────────────

  private drawPageTitle(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, pad: number, title: string, L: Layout): void {
    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.titleFont + 2}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(title, sx + w / 2, sy + pad + 12);
    const tw = ctx.measureText(title).width;
    ctx.strokeStyle = 'rgba(212,175,55,0.5)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(sx + w / 2 - tw / 2 - 15, sy + pad + 22); ctx.lineTo(sx + w / 2 + tw / 2 + 15, sy + pad + 22); ctx.stroke();
  }

  private drawSectionHead(ctx: CanvasRenderingContext2D, x: number, y: number, _w: number, title: string, L: Layout): number {
    ctx.fillStyle = COLORS.accent;
    ctx.font = `bold ${L.titleFont}px Arial`;
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText(title, x + 5, y);
    const tw = ctx.measureText(title).width;
    ctx.strokeStyle = 'rgba(212,175,55,0.3)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x + 5, y + L.titleFont + 4); ctx.lineTo(x + tw + 15, y + L.titleFont + 4); ctx.stroke();
    return y + L.titleFont + 12;
  }

  private drawBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, label: string, cur: number, max: number, fill: string, bg: string, L: Layout): number {
    ctx.fillStyle = bg;
    this.drawRoundedRect(ctx, x, y, w, h, 4); ctx.fill();
    const pct = max > 0 ? Math.min(1, cur / max) : 0;
    if (pct > 0) { ctx.fillStyle = fill; this.drawRoundedRect(ctx, x, y, w * pct, h, 4); ctx.fill(); }
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1;
    this.drawRoundedRect(ctx, x, y, w, h, 4); ctx.stroke();
    ctx.fillStyle = '#fff'; ctx.font = `bold ${L.font - 1}px Arial`;
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(label, x + 6, y + h / 2);
    ctx.textAlign = 'right';
    ctx.fillText(`${cur} / ${max}`, x + w - 6, y + h / 2);
    return y + h;
  }

  private drawStatRow(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, label: string, value: string, color: string, L: Layout): number {
    ctx.fillStyle = '#999'; ctx.font = `${L.font}px Arial`;
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(label, x, y + 10);
    ctx.fillStyle = color; ctx.font = `bold ${L.font}px Arial`;
    ctx.textAlign = 'right';
    ctx.fillText(value, x + w, y + 10);
    return y + 20;
  }

  // ── Slot rendering ──────────────────────────────────────────────────────

  private drawSlot(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, item: GameItem | null, hov: boolean, sel: boolean): void {
    // Background
    ctx.fillStyle = sel ? '#3a3530' : hov ? COLORS.hover : COLORS.panelBg;
    this.drawRoundedRect(ctx, x, y, sz, sz, 4);
    ctx.fill();

    ctx.strokeStyle = sel ? COLORS.gold : hov ? '#554433' : COLORS.sectionBorder;
    ctx.lineWidth = sel ? 2 : 1;
    ctx.stroke();

    if (!item) {
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = COLORS.slotBorderEmpty; ctx.lineWidth = 1;
      this.drawRoundedRect(ctx, x + 3, y + 3, sz - 6, sz - 6, 3);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      return;
    }

    const color = getItemColor(item.rarity);

    // Rarity glow
    ctx.fillStyle = color + '20';
    this.drawRoundedRect(ctx, x + 2, y + 2, sz - 4, sz - 4, 3);
    ctx.fill();

    // Icon rendering chain: asset -> procedural detailed -> procedural -> emoji
    let drawn = false;
    if (this.assetLoader && item.icon) {
      const img = this.assetLoader.getImage(`item_icon:${item.icon}`);
      if (img) { const p = Math.floor(sz * 0.1); ctx.drawImage(img, x + p, y + p, sz - p * 2, sz - p * 2); drawn = true; }
    }
    if (!drawn && 'slot' in item) {
      const eq = item as EquipmentItem;
      const proc = getDetailedProceduralIcon(eq.slot, item.rarity, eq.weaponSubType, eq.defenseType,
        eq.prefixes?.[0]?.type?.includes('Damage') ? eq.prefixes[0].type.replace('Damage', '').replace('flat', '').toLowerCase() : undefined);
      if (proc) { const p = Math.floor(sz * 0.1); ctx.drawImage(proc, x + p, y + p, sz - p * 2, sz - p * 2); drawn = true; }
    }
    if (!drawn) {
      ctx.fillStyle = color + 'aa';
      this.drawRoundedRect(ctx, x + 4, y + 4, sz - 8, sz - 8, 3); ctx.fill();
      if ('slot' in item) {
        ctx.fillStyle = '#fff'; ctx.font = `bold ${Math.floor(sz * 0.35)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(this.getSlotIcon((item as EquipmentItem).slot), x + sz / 2, y + sz / 2);
      }
    }

    // Rarity border glow
    ctx.strokeStyle = color + '60'; ctx.lineWidth = 1;
    this.drawRoundedRect(ctx, x + 2, y + 2, sz - 4, sz - 4, 3); ctx.stroke();

    // Level badge
    if ('level' in item && sz >= 32) {
      const bs = Math.max(12, sz * 0.3);
      ctx.fillStyle = 'rgba(0,0,0,0.85)';
      this.drawRoundedRect(ctx, x + sz - bs - 1, y + sz - bs + 1, bs, bs - 2, 2); ctx.fill();
      ctx.fillStyle = color; ctx.font = `bold ${Math.max(8, sz * 0.2)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText((item as EquipmentItem).level.toString(), x + sz - bs / 2 - 1, y + sz - bs / 2 + 1);
    }
  }

  private drawEquipmentSlot(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, item: GameItem | null, hov: boolean, sel: boolean, slot: EquipmentSlot): void {
    // Gradient background
    const g = ctx.createLinearGradient(x, y, x, y + sz);
    if (item) {
      const rg = this.getRarityGradient(item.rarity);
      g.addColorStop(0, rg.top); g.addColorStop(1, rg.bottom);
    } else {
      g.addColorStop(0, hov ? COLORS.hover : COLORS.panelBg);
      g.addColorStop(1, hov ? '#1f1b18' : '#121010');
    }
    ctx.fillStyle = g;
    this.drawRoundedRect(ctx, x, y, sz, sz, 5); ctx.fill();

    // Border
    if (item) {
      const bc = getItemColor(item.rarity);
      ctx.strokeStyle = sel ? '#fff' : hov ? bc : bc + '99';
      ctx.lineWidth = sel ? 2.5 : hov ? 2 : 1.5;
      if (item.rarity !== 'normal' && item.rarity !== 'magic') { ctx.shadowColor = bc; ctx.shadowBlur = hov ? 12 : 6; }
    } else {
      ctx.strokeStyle = sel ? COLORS.accent : hov ? '#5a4a3a' : '#3a3028';
      ctx.lineWidth = sel ? 2 : 1;
    }
    ctx.stroke();
    ctx.shadowBlur = 0;

    if (item) {
      // Radial glow
      const color = getItemColor(item.rarity);
      const rg = ctx.createRadialGradient(x + sz / 2, y + sz / 2, 0, x + sz / 2, y + sz / 2, sz / 2);
      rg.addColorStop(0, color + '40'); rg.addColorStop(1, 'transparent');
      ctx.fillStyle = rg; ctx.fillRect(x, y, sz, sz);

      // Icon rendering chain
      let drawn = false;
      if (this.assetLoader && item.icon) {
        const img = this.assetLoader.getImage(`item_icon:${item.icon}`);
        if (img) { const p = Math.floor(sz * 0.08); ctx.drawImage(img, x + p, y + p, sz - p * 2, sz - p * 2); drawn = true; }
      }
      if (!drawn && 'slot' in item) {
        const proc = getProceduralIcon((item as EquipmentItem).slot, item.rarity);
        if (proc) { const p = Math.floor(sz * 0.08); ctx.drawImage(proc, x + p, y + p, sz - p * 2, sz - p * 2); drawn = true; }
      }
      if (!drawn && 'slot' in item) {
        ctx.fillStyle = '#fff'; ctx.font = `bold ${Math.floor(sz * 0.4)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(this.getSlotIcon((item as EquipmentItem).slot), x + sz / 2, y + sz / 2);
      }

      // Level badge
      if ('level' in item) {
        const bs = Math.max(14, sz * 0.32);
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        this.drawRoundedRect(ctx, x + sz - bs - 2, y + sz - bs, bs, bs - 2, 3); ctx.fill();
        ctx.fillStyle = getItemColor(item.rarity);
        ctx.font = `bold ${Math.max(9, sz * 0.22)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText((item as EquipmentItem).level.toString(), x + sz - bs / 2 - 2, y + sz - bs / 2);
      }
    } else {
      // Empty slot: fallback icon
      let drawn = false;
      const fallback = SLOT_FALLBACK_ICONS[slot];
      if (this.assetLoader && fallback) {
        const img = this.assetLoader.getImage(`item_icon:${fallback}`);
        if (img) { ctx.globalAlpha = 0.3; const p = 6; ctx.drawImage(img, x + p, y + p, sz - p * 2, sz - p * 2); ctx.globalAlpha = 1; drawn = true; }
      }
      if (!drawn) {
        const proc = getProceduralIcon(slot, 'normal');
        if (proc) { ctx.globalAlpha = 0.3; const p = 6; ctx.drawImage(proc, x + p, y + p, sz - p * 2, sz - p * 2); ctx.globalAlpha = 1; drawn = true; }
      }
      if (!drawn) {
        ctx.fillStyle = '#3a352f'; ctx.font = `${Math.floor(sz * 0.38)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(this.getSlotIcon(slot), x + sz / 2, y + sz / 2);
      }

      // Slot label
      ctx.fillStyle = '#4a453f'; ctx.font = `${Math.max(8, sz * 0.2)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(this.getSlotLabel(slot), x + sz / 2, y + sz + 2);
    }
  }

  private getRarityGradient(rarity: string): { top: string; bottom: string } {
    switch (rarity) {
      case 'legendary': return { top: '#3d2a1a', bottom: '#2a1a0a' };
      case 'set': return { top: '#1a2a1a', bottom: '#0a1a0a' };
      case 'rare': return { top: '#2a2a1a', bottom: '#1a1a0a' };
      case 'magic': return { top: '#1a1a2a', bottom: '#0a0a1a' };
      default: return { top: COLORS.panelBg, bottom: '#121010' };
    }
  }

  // ── Tooltip ─────────────────────────────────────────────────────────────

  private renderTooltip(ctx: CanvasRenderingContext2D, L: Layout): void {
    // Item / currency tooltip
    if (this.subPage === 'items' || this.subPage === 'materials') {
      if (this.hoveredSlot && !this.selectedSlot) {
        const item = this.getItemInSlot(this.hoveredSlot);
        if (item) {
          let compare: EquipmentItem | null = null;
          if (this.hoveredSlot.type === 'inventory' && 'slot' in item) {
            compare = this.player.inventory.getEquippedItem((item as EquipmentItem).slot) as EquipmentItem | null;
          }
          const setIds: string[] = [];
          const eqSlots: EquipmentSlot[] = ['head', 'chest', 'gloves', 'belt', 'boots', 'weapon', 'offhand', 'amulet', 'ring1', 'ring2', 'cape'];
          for (const s of eqSlots) {
            const eq = this.player.inventory.getEquippedItem(s) as EquipmentItem | null;
            if (eq?.setId) setIds.push(eq.setId);
          }
          this.itemTooltip.setEquippedSetIds(setIds);
          this.itemTooltip.show(item, this.mx, this.my, compare);
          this.itemTooltip.render(ctx, ctx.canvas.width, ctx.canvas.height);
          this.itemTooltip.hide();
        } else if (this.hoveredSlot.type === 'currency' && this.hoveredSlot.currencyType) {
          this.renderCurrencyTooltip(ctx, this.hoveredSlot.currencyType, L);
        }
      }
    }

    // Stone tooltip
    if (this.subPage === 'stones' && this.hoveredStoneIdx >= 0) {
      const stones = this.player.inventory.getEffectStones();
      if (this.hoveredStoneIdx < stones.length) this.renderStoneTooltip(ctx, stones[this.hoveredStoneIdx], L);
    }

    // Materials page currency tooltip
    if (this.subPage === 'materials' && this.hoveredMatIdx >= 0) {
      if (this.hoveredMatIdx < CURRENCIES.length) this.renderCurrencyTooltip(ctx, CURRENCIES[this.hoveredMatIdx], L);
    }
  }

  private renderStoneTooltip(ctx: CanvasRenderingContext2D, stone: EffectStone, L: Layout): void {
    const elemClr: Record<string, string> = { od: '#ff4422', gok: '#4488ff', su: '#44dddd', yer: '#88aa44', kut: '#aa44ff' };
    const ec = elemClr[stone.type] || '#aaa';

    const lines: { text: string; color: string }[] = [
      { text: stone.name, color: getItemColor(stone.rarity) },
      { text: `Tur: ${stone.type.charAt(0).toUpperCase() + stone.type.slice(1)}`, color: ec },
      { text: `Nadirlik: ${stone.rarity}`, color: getItemColor(stone.rarity) },
    ];

    if (stone.effect) {
      lines.push({ text: '---', color: '#333' });
      if (stone.effect.description) lines.push({ text: stone.effect.description, color: '#aaa' });
      if (stone.effect.damageBonus) lines.push({ text: `+${stone.effect.damageBonus}% Hasar`, color: '#88ff88' });
      if (stone.effect.areaBonus) lines.push({ text: `+${stone.effect.areaBonus}% Alan`, color: '#88ff88' });
      if (stone.effect.cooldownReduction) lines.push({ text: `-${stone.effect.cooldownReduction}% Bekleme`, color: '#88ff88' });
      if (stone.effect.manaCostReduction) lines.push({ text: `-${stone.effect.manaCostReduction}% Mana Maliyeti`, color: '#88ff88' });
      if (stone.effect.lifesteal) lines.push({ text: `${stone.effect.lifesteal}% Can Emme`, color: '#ff6666' });
      if (stone.effect.slow) lines.push({ text: `${stone.effect.slow}% Yavaslat`, color: '#88ccff' });
      if (stone.effect.knockback) lines.push({ text: `${stone.effect.knockback} Geri Itme`, color: '#cc8844' });
      if (stone.effect.dot) lines.push({ text: `${stone.effect.dot.damage} Hasar / ${stone.effect.dot.duration}sn`, color: '#ff8800' });
      if (stone.effect.chainCount) lines.push({ text: `+${stone.effect.chainCount} Zincir`, color: '#3498db' });
    }

    this.drawTooltipBox(ctx, lines, ec, L);
  }

  private renderCurrencyTooltip(ctx: CanvasRenderingContext2D, type: CurrencySlotType, L: Layout): void {
    const info = getCurrencySlotInfo(type);
    const amt = this.player.inventory.getCurrencyAmount(type);
    const lines: { text: string; color: string }[] = [
      { text: info.name, color: info.color },
      { text: `${t('crafting.materials')} ${amt}`, color: '#aaa' },
    ];
    this.drawTooltipBox(ctx, lines, info.color, L);
  }

  private drawTooltipBox(ctx: CanvasRenderingContext2D, lines: { text: string; color: string }[], borderColor: string, L: Layout): void {
    const pad = 10;
    ctx.font = `${L.font}px Arial`;
    let maxW = 0;
    for (const l of lines) maxW = Math.max(maxW, ctx.measureText(l.text).width);

    const w = maxW + pad * 2;
    const h = lines.length * (L.font + 5) + pad * 2;
    let tx = this.mx + 15;
    let ty = this.my - h - 10;
    if (ty < this.bounds.y) ty = this.my + 15;
    if (tx + w > this.bounds.x + this.bounds.width) tx = this.mx - w - 15;

    ctx.fillStyle = 'rgba(10,8,6,0.95)';
    this.drawRoundedRect(ctx, tx, ty, w, h, 5); ctx.fill();
    ctx.strokeStyle = borderColor; ctx.lineWidth = 1.5; ctx.stroke();

    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    let ly = ty + pad;
    for (const l of lines) {
      ctx.fillStyle = l.color; ctx.font = `${L.font}px Arial`;
      ctx.fillText(l.text, tx + pad, ly);
      ly += L.font + 5;
    }
  }

  // ── Mouse handling ──────────────────────────────────────────────────────

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;
    this.mx = e.x;
    this.my = e.y;

    if (e.type === 'move') {
      this.hoveredSlot = this.getSlotAt(e.x, e.y);
      this.hoveredBtn = this.getButtonAt(e.x, e.y);
      this.hoveredSubPage = this.getSubPageBtnAt(e.x, e.y);
    }

    if (e.type === 'wheel' && this.subPage === 'character' && e.deltaY !== undefined) {
      this.charScroll = Math.max(0, this.charScroll + e.deltaY * 0.5);
      return true;
    }

    // Shift+right-click: delete
    if (e.type === 'down' && e.button === 'right' && e.shiftKey) {
      const slot = this.getSlotAt(e.x, e.y);
      if (slot?.type === 'inventory') {
        this.handleDeleteRequest(slot.index, slot.tab ?? this.player.inventory.getActiveTab());
        return true;
      }
    }

    // Right-click: quick equip/unequip
    if (e.type === 'down' && e.button === 'right') {
      const slot = this.getSlotAt(e.x, e.y);
      if (slot) { this.handleRightClick(slot); return true; }
    }

    if (e.type === 'down' && e.button === 'left') {
      // Sub-page navigation
      const sp = this.getSubPageBtnAt(e.x, e.y);
      if (sp) {
        this.subPage = sp as SubPageType;
        this.hoveredSlot = null; this.selectedSlot = null;
        this.charScroll = 0; this.hoveredStoneIdx = -1; this.hoveredMatIdx = -1;
        this.delSlot = null; this.delTimer = 0;
        this._layout = null;
        return true;
      }

      // Button clicks (items page only)
      if (this.subPage === 'items') {
        const btn = this.getButtonAt(e.x, e.y);
        if (btn) { this.handleButtonClick(btn); return true; }
      }

      // Delete confirmation
      const slot = this.getSlotAt(e.x, e.y);
      if (slot?.type === 'inventory' && this.delSlot) {
        const tab = slot.tab ?? this.player.inventory.getActiveTab();
        if (this.delSlot.index === slot.index && this.delSlot.tab === tab) {
          this.player.inventory.destroyItem(slot.index, tab);
          this.delSlot = null; this.delTimer = 0;
          return true;
        }
      }

      // Delete X button
      if (slot?.type === 'inventory' && this.subPage === 'items' && this.isDeleteBtnHit(e.x, e.y, slot)) {
        this.handleDeleteRequest(slot.index, slot.tab ?? this.player.inventory.getActiveTab());
        return true;
      }

      // Clear delete state
      if (this.delSlot) { this.delSlot = null; this.delTimer = 0; }

      // Select slot
      if (slot) { this.selectedSlot = slot; return true; }
    }

    if (e.type === 'up' && this.selectedSlot) {
      const target = this.getSlotAt(e.x, e.y);
      if (target) this.handleSlotInteraction(this.selectedSlot, target);
      this.selectedSlot = null;
      return true;
    }

    return false;
  }

  // ── Interaction handlers ────────────────────────────────────────────────

  private handleRightClick(slot: SlotInfo): void {
    if (slot.type === 'inventory') {
      const item = this.player.inventory.getItem(slot.index, slot.tab);
      if (item && 'slot' in item) {
        const eq = item as EquipmentItem;
        let target = eq.slot;
        if (this.player.inventory.isRingSlot(eq.slot)) {
          const r1 = this.player.inventory.getEquippedItem('ring1');
          const r2 = this.player.inventory.getEquippedItem('ring2');
          target = !r1 ? 'ring1' : !r2 ? 'ring2' : 'ring1';
        }
        const prev = this.player.equipItem(eq, target);
        this.player.inventory.removeItem(slot.index, slot.tab);
        if (prev) this.player.inventory.addItem(prev);
        getAudioManager().play('item_equip');
      }
    } else if (slot.type === 'equipment' && slot.equipSlot) {
      const item = this.player.unequipItem(slot.equipSlot);
      if (item) { this.player.inventory.addItem(item); getAudioManager().play('item_drop'); }
    }
  }

  private handleDeleteRequest(index: number, tab: number): void {
    const item = this.player.inventory.getItem(index, tab);
    if (!item) return;
    if ('itemType' in item && (item as { itemType: string }).itemType === 'quest') return;

    if (this.delSlot?.index === index && this.delSlot.tab === tab) {
      this.player.inventory.destroyItem(index, tab);
      this.delSlot = null; this.delTimer = 0;
      return;
    }

    this.delSlot = { index, tab };
    this.delTimer = InventoryTab.DEL_TIMEOUT;
  }

  private isDeleteBtnHit(mx: number, my: number, slot: SlotInfo): boolean {
    if (slot.type !== 'inventory') return false;
    const item = this.player.inventory.getItem(slot.index, slot.tab);
    if (!item) return false;
    if ('itemType' in item && (item as { itemType: string }).itemType === 'quest') return false;

    const L = this.getLayout();
    const s = L.invSlot;
    const bs = Math.max(12, s * 0.3);
    const cx = slot.x + s - bs / 2 - 1;
    const cy = slot.y + 1 + bs / 2;
    const dx = mx - cx;
    const dy = my - cy;
    return dx * dx + dy * dy <= (bs / 2) * (bs / 2);
  }

  private handleButtonClick(btn: string): void {
    if (btn.startsWith('tab_')) {
      if (btn === 'tab_add') {
        if (this.player.inventory.purchaseTab()) debugLog('Inventory', 'Purchased new inventory tab!');
      } else {
        this.player.inventory.setActiveTab(parseInt(btn.split('_')[1]));
      }
      return;
    }
    if (btn.startsWith('filter_')) { this.filter = btn.split('_')[1] as FilterType; return; }
    if (btn.startsWith('sort_')) {
      const st = btn.split('_')[1] as SortType;
      if (st === this.sort) { this.sort = 'none'; }
      else { this.sort = st; if (st !== 'none') this.player.inventory.sortItems(st as 'rarity' | 'level' | 'type' | 'name'); }
    }
  }

  private handleSlotInteraction(src: SlotInfo, tgt: SlotInfo): void {
    if (src.type === tgt.type && src.index === tgt.index && src.tab === tgt.tab) return;

    if (src.type === 'inventory' && tgt.type === 'equipment' && tgt.equipSlot) {
      const item = this.player.inventory.getItem(src.index, src.tab);
      if (item && 'slot' in item) {
        const eq = item as EquipmentItem;
        if (this.player.inventory.canEquipToSlot(eq, tgt.equipSlot)) {
          const prev = this.player.equipItem(eq, tgt.equipSlot);
          this.player.inventory.removeItem(src.index, src.tab);
          if (prev) this.player.inventory.addItem(prev);
          getAudioManager().play('item_equip');
        }
      }
    }

    if (src.type === 'equipment' && tgt.type === 'inventory' && src.equipSlot) {
      const item = this.player.unequipItem(src.equipSlot);
      if (item) { this.player.inventory.addItem(item); getAudioManager().play('item_drop'); }
    }

    if (src.type === 'inventory' && tgt.type === 'inventory') {
      this.player.inventory.moveItem(src.index, tgt.index, src.tab, tgt.tab);
    }
  }

  // ── Hit detection ───────────────────────────────────────────────────────

  private getSubPageBtnAt(x: number, y: number): string | null {
    const L = this.getLayout();
    const btnStartY = L.contentY + 10;
    const pages = subPages();

    for (let i = 0; i < pages.length; i++) {
      const bx = L.sidebarX + (SIDEBAR_W - SIDEBAR_BTN) / 2;
      const by = btnStartY + i * (SIDEBAR_BTN + SIDEBAR_GAP);
      if (x >= bx && x <= bx + SIDEBAR_BTN && y >= by && y <= by + SIDEBAR_BTN) return pages[i].id;
    }
    return null;
  }

  private getButtonAt(x: number, y: number): string | null {
    if (this.subPage !== 'items') return null;
    const L = this.getLayout();
    const invX = L.contentX + 10;
    const tabY = L.contentY + 8;
    const tabCount = this.player.inventory.getTabCount();
    const tw = Math.min(60, (L.invAreaW - 100) / Math.max(tabCount + 1, 5));
    const gap = 4;

    for (let i = 0; i < tabCount; i++) {
      const tx = invX + i * (tw + gap);
      if (x >= tx && x <= tx + tw && y >= tabY && y <= tabY + L.tabBarH) return `tab_${i}`;
    }
    if (tabCount < 10) {
      const ax = invX + tabCount * (tw + gap);
      if (x >= ax && x <= ax + tw && y >= tabY && y <= tabY + L.tabBarH) return 'tab_add';
    }

    const ctrlY = tabY + L.tabBarH + 8;
    if (y >= ctrlY && y <= ctrlY + L.btnH) {
      let bx = invX + 45;
      for (const f of filterOptions()) {
        const bw = 50;
        if (x >= bx && x <= bx + bw) return `filter_${f.id}`;
        bx += bw + 4;
      }
      bx += 60;
      for (const s of sortOptions()) {
        const bw = 50;
        if (x >= bx && x <= bx + bw) return `sort_${s.id}`;
        bx += bw + 4;
      }
    }
    return null;
  }

  private getSlotAt(x: number, y: number): SlotInfo | null {
    return this.getEquipSlotAt(x, y) ||
      (this.subPage === 'items' ? (this.getInvSlotAt(x, y) || this.getCurrSlotAt(x, y)) : null);
  }

  private getEquipSlotAt(x: number, y: number): SlotInfo | null {
    const L = this.getLayout();
    const s = L.equipSlot;
    const cx = L.equipX + L.equipW / 2;
    const bodyY = L.contentY + 42;

    for (const p of this.getEquipSlotPositions(cx, bodyY, s)) {
      if (x >= p.x && x <= p.x + s && y >= p.y && y <= p.y + s) {
        return { x: p.x, y: p.y, size: s, index: 0, type: 'equipment', equipSlot: p.slot };
      }
    }
    return null;
  }

  private getInvSlotAt(x: number, y: number): SlotInfo | null {
    const L = this.getLayout();
    const s = L.invSlot;
    const gap = 4;
    const cols = L.cols;
    const invX = L.contentX + 10;
    const gridW = cols * (s + gap) - gap;
    const gx = invX + (L.invAreaW - 20 - gridW) / 2;
    const gy = L.contentY + 8 + L.tabBarH + 8 + L.btnH + 12;
    const maxSlots = this.player.inventory.getMaxSlots();
    const rows = Math.ceil(maxSlots / cols);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        if (idx >= maxSlots) continue;
        const sx = gx + c * (s + gap);
        const sy = gy + r * (s + gap);
        if (x >= sx && x <= sx + s && y >= sy && y <= sy + s) {
          return { x: sx, y: sy, size: s, index: idx, type: 'inventory', tab: this.player.inventory.getActiveTab() };
        }
      }
    }
    return null;
  }

  private getCurrSlotAt(x: number, y: number): SlotInfo | null {
    const L = this.getLayout();
    const ss = L.currSlot;
    const gap = 8;
    const invX = L.contentX + 10;
    const areaW = L.invAreaW - 20;
    const s = L.invSlot;
    const cols = L.cols;
    const maxSlots = this.player.inventory.getMaxSlots();
    const rows = Math.ceil(maxSlots / cols);
    const gridH = rows * (s + 4) + 16;
    const headerH = L.tabBarH + 8 + L.btnH + 12 + 8;
    const matY = L.contentY + headerH + gridH + 12 + 32;
    const matCols = Math.floor(areaW / (ss + gap));

    for (let i = 0; i < CURRENCIES.length; i++) {
      const col = i % matCols;
      const row = Math.floor(i / matCols);
      const sx = invX + col * (ss + gap);
      const sy = matY + row * (ss + gap + 14);
      if (x >= sx && x <= sx + ss && y >= sy && y <= sy + ss) {
        return { x: sx, y: sy, size: ss, index: i, type: 'currency', currencyType: CURRENCIES[i] };
      }
    }
    return null;
  }

  // ── Data helpers ────────────────────────────────────────────────────────

  private getItemInSlot(slot: SlotInfo): GameItem | null {
    if (slot.type === 'inventory') return this.player.inventory.getItem(slot.index, slot.tab);
    if (slot.type === 'equipment' && slot.equipSlot) return this.player.inventory.getEquippedItem(slot.equipSlot);
    return null;
  }

  private getFilteredItems(): (GameItem | null)[] {
    const all = this.player.inventory.getAllItems();
    if (this.filter === 'all') return all;

    return all.map(item => {
      if (!item || !('slot' in item)) return item;
      const eq = item as EquipmentItem;
      switch (this.filter) {
        case 'weapon': return WEAPON_SLOTS.includes(eq.slot) ? item : null;
        case 'armor': return ARMOR_SLOTS.includes(eq.slot) ? item : null;
        case 'accessory': return ACCESSORY_SLOTS.includes(eq.slot) ? item : null;
        default: return item;
      }
    });
  }

  private getStatNames(): Record<string, string> {
    return {
      damage: t('items.mods.damage'), spellDamage: t('items.mods.spellDamage'),
      attackSpeed: t('items.mods.attackSpeed'), critChance: t('items.mods.critChance'),
      critDamage: t('items.mods.critDamage'), maxHp: t('items.mods.maxHp'),
      maxMana: t('items.mods.maxMana'), armor: t('items.mods.armor'),
      evasion: t('items.mods.evasion'), energyShield: t('items.mods.energyShield'),
      moveSpeed: t('items.mods.moveSpeed'), strength: t('items.mods.strength'),
      agility: t('items.mods.agility'), spirit: t('items.mods.spirit'),
      allResist: t('items.mods.allResist'), gokResist: t('items.mods.gokResist'),
      odResist: t('items.mods.odResist'), suResist: t('items.mods.suResist'),
      karanlikResist: t('items.mods.karanlikResist'), lifeRegen: t('items.mods.lifeRegen'),
      manaRegen: t('items.mods.manaRegen'), physicalDamage: t('items.mods.physicalDamage'),
      elementalDamage: t('items.mods.elementalDamage'), castSpeed: t('items.mods.castSpeed'),
      hpRegen: t('items.mods.hpRegen'), lifeLeech: t('items.mods.lifeLeech'),
      manaLeech: t('items.mods.manaLeech'), blockChance: t('items.mods.blockChance'),
      cooldownReduction: t('items.mods.cooldownReduction'), dodgeChance: t('items.mods.dodgeChance'),
      physicalResist: t('items.mods.physicalResist'),
    };
  }

  // ── Utility ─────────────────────────────────────────────────────────────

  private getSlotLabel(slot: EquipmentSlot): string {
    const labels: Record<EquipmentSlot, string> = {
      head: t('inventory.helmet'), chest: t('inventory.chestArmor'),
      gloves: t('inventory.gloves'), belt: t('inventory.belt'),
      boots: t('inventory.boots'), weapon: t('inventory.weapon'),
      offhand: t('inventory.shield'), amulet: t('inventory.amulet'),
      ring1: t('inventory.ring'), ring2: t('inventory.ring'),
      cape: t('inventory.cape'),
    };
    return labels[slot] || slot;
  }

  private getSlotIcon(slot: EquipmentSlot): string {
    const icons: Record<EquipmentSlot, string> = {
      head: '\u26D1', chest: '\uD83D\uDEE1', gloves: '\uD83E\uDDE4', belt: '\uD83D\uDCFF',
      boots: '\uD83D\uDC62', weapon: '\u2694', offhand: '\uD83D\uDEE1', amulet: '\uD83D\uDC8E',
      ring1: '\uD83D\uDC8D', ring2: '\uD83D\uDC8D', cape: '\uD83E\uDDE3',
    };
    return icons[slot] || '?';
  }
}
